import { useRef, useState } from 'react';

import cx from 'classnames';
import type { GroupBase } from 'react-select-5';

import { FormFieldControlledTooltip } from 'hooks/useFormFieldPopup';

import { Select as FeatureRichSelect } from '../..';
import { useOptionToString } from '../../hooks/useOptionToString';
import { ReactSelectRef } from '../../index.types';
import s from './index.module.css';
import { FinalFormSelectProps } from './types';

/*
  React Select v5 only supports values as Options.
  Lib maintainers suggest to use controlled components
  that manage values as Options and convert them to strings
  https://github.com/JedWatson/react-select/issues/3063
  https://github.com/JedWatson/react-select/issues/3761
  https://github.com/JedWatson/react-select/issues/2920
*/

/**
 * Pass this adapter to Final Form's `Field` component along with `FormGroupAdapter`.
 * @example
 * <Field component={FormGroupAdapter} customComponent={Select} />
 */
export function Select<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: FinalFormSelectProps<Option, IsMulti, Group>) {
  const { optionToString, getOptionValue, value, onChange, ...rest } = props;
  const [tooltipShown, setTooltipShown] = useState(false);

  const selectRef: ReactSelectRef<Option, IsMulti, Group> = useRef(null);

  const ots = useOptionToString<Option, IsMulti, Group>(
    {
      value: {
        value: optionToString ? value : undefined,
        setValue: optionToString ? onChange : () => {},
      },
      selectRef,
    },
    {
      enabled: optionToString,
      isMulti: props.isMulti,
      getOptionValue: props.getOptionValue,
      options: props.options,
      ...(props.async && { async: props.async, loadOptions: props.loadOptions }),
    },
  );

  return (
    <>
      <FeatureRichSelect<Option, IsMulti, Group>
        getOptionValue={getOptionValue}
        {...rest} // Customized props past this point ↓↓↓ When customizing, make sure to provide an interface for the component users.
        {...(ots.enabled
          ? { ...ots.controls, async: rest.async as true }
          : {
              value: optionToString ? ots.controls?.value : value,
              onChange: optionToString ? ots.controls?.onChange : onChange,
            })}
        onFocus={(e) => {
          setTooltipShown(true);
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setTooltipShown(false);
          props.onBlur?.(e);
        }}
        selectRef={(node) => {
          selectRef.current = node;
          if (props.selectRef) {
            if (typeof props.selectRef === 'function') {
              props.selectRef(node);
              return;
            }
            props.selectRef.current = node;
          }
        }}
        classNames={{
          ...props.classNames,
          container: (p) => cx(s.container, props.classNames?.container?.(p)),
          control: (p) => cx(s.control, { [s.error]: props.isError }, props.classNames?.control?.(p)),
        }}
      />
      {props.tooltipContent && (
        <FormFieldControlledTooltip
          content={props.tooltipContent}
          isOpen={tooltipShown}
          anchorRef={{ current: selectRef.current?.inputRef }}
          onClose={() => setTooltipShown(false)}
        />
      )}
    </>
  );
}
